import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import type {RootState} from '../../../store';
import {showLoader, hideLoader} from '../../../store/loaderSlice';
import {showErrorModal} from '../../../store/errorSlice';
import {openQuickAssistScreen} from '../../../store/quickAssistSlice';
import {getQuickAssistTechnicians} from '../../../../features/dashboard/screens/DashboardScreen/DashboardController';
import {definedBeginShiftErrorMessages} from '../../../../features/repairDetails/constants/repairDetails.constant';
import type {QuickAssistRequest} from '../../../../features/dashboard/components/WorkOrders/WorkOrderProps';

export interface UseQuickAssistResult {
  handleQuickAssistPress: (payload: QuickAssistRequest) => Promise<void>;
}

export const useQuickAssist = (): UseQuickAssistResult => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {selectedTechnician} = useSelector((state: RootState) => state.technician);

  const handleQuickAssistPress = useCallback(
    async (payload: QuickAssistRequest) => {
      const technicianId = Number(selectedTechnician?.selectedTechnicianId);

      if (!technicianId) {
        return;
      }

      try {
        dispatch(showLoader());

        const result = await getQuickAssistTechnicians(technicianId, payload);
        if (typeof result === 'string') {
          if (result.includes(definedBeginShiftErrorMessages.woThreshold)) {
            dispatch(
              showErrorModal({
                title: t('repairDetails.spendingLimitTitle'),
                subTitle: t('repairDetails.spendingLimitMessage'),
                message: '',
                noTranslation: true,
                ctas: [
                  {
                    label: t('repairDetails.cancelAuthorizationCTA'),
                    type: 'CANCEL_SPENDING_LIMIT',
                    primary: false,
                  },
                  {
                    label: t('repairDetails.requestAuthorizationCTA'),
                    type: 'YES',
                  },
                ],
                screen: 'repairCard',
              }),
            );
          } else {
            dispatch(
              showErrorModal({
                subTitle: result,
                title: t('createFlow.common.modalTitles.error'),
                message: '',
                noTranslation: true,
                ctas: [{label: 'OK', type: 'DISMISS'}],
                screen: 'repairCard',
              }),
            );
          }
        } else if (result) {
          // Dispatch initial data to Redux and signal MainLayoutScreen to show the screen.
          dispatch(
            openQuickAssistScreen({
              initialTechnicians: result.items ?? [],
              initialPagination: result.pagination ?? null,
              initialShopId: payload.shopId,
              initialPageSize: payload.pageSize,
            }),
          );
        }
      } catch {
        // Error already handled by globalErrorHandler inside getQuickAssistTechnicians
      } finally {
        dispatch(hideLoader());
      }
    },
    [dispatch, selectedTechnician, t],
  );

  return {handleQuickAssistPress};
};